import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMySanctions, SanctionDto } from '../../api/payment/brand-sanction.api';
import { isBlockingSanction, SANCTION_CHANGED_EVENT } from '../../utils/brand-sanction';

// Trạng thái xử phạt của người đặt đơn đang đăng nhập (RB7). Mọi nút tạo việc nằm sâu trong mục Hợp tác creator
// đọc `blocked` từ đây thay vì truyền prop qua nhiều tầng. Ngoài provider thì không chặn gì (BE vẫn chặn thật).

export interface OrdererSanctionState {
  active: SanctionDto | null;
  history: SanctionDto[];
  /** true khi đang bị khoá tạo việc (khoá còn hạn, vĩnh viễn, hoặc cờ khoá sàn cũ của tài khoản). */
  blocked: boolean;
  /** Khoá sàn cũ (AdAccount.marketplaceBanned) khi chưa có lần phạt mới nào đang hiệu lực. */
  legacyBan: { reason: string | null } | null;
  reload: () => void;
}

const NOT_BLOCKED: OrdererSanctionState = { active: null, history: [], blocked: false, legacyBan: null, reload: () => undefined };

const OrdererSanctionContext = createContext<OrdererSanctionState>(NOT_BLOCKED);

export const useOrdererSanction = () => useContext(OrdererSanctionContext);

interface ProviderProps {
  /** false thì không gọi API (vd người chưa có tài khoản đặt đơn). Mặc định true. */
  enabled?: boolean;
  /** Tài khoản còn cờ khoá sàn cũ (AdAccount.marketplaceBanned). */
  legacyBanned?: boolean;
  legacyBanReason?: string | null;
  children: React.ReactNode;
}

export const OrdererSanctionProvider: React.FC<ProviderProps> = ({
  enabled = true,
  legacyBanned = false,
  legacyBanReason = null,
  children,
}) => {
  const [active, setActive] = useState<SanctionDto | null>(null);
  const [history, setHistory] = useState<SanctionDto[]>([]);

  const reload = useCallback(() => {
    if (!enabled) return;
    getMySanctions()
      .then((res) => {
        setActive(res.active);
        setHistory(res.history ?? []);
      })
      // Không tải được thì giữ trạng thái cũ: BE vẫn chặn và FE hiện đúng câu lỗi BE.
      .catch(() => undefined);
  }, [enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    window.addEventListener(SANCTION_CHANGED_EVENT, reload);
    return () => window.removeEventListener(SANCTION_CHANGED_EVENT, reload);
  }, [reload]);

  const value = useMemo<OrdererSanctionState>(() => {
    const sanctionBlocked = isBlockingSanction(active, Date.now());
    return {
      active,
      history,
      blocked: sanctionBlocked || legacyBanned,
      legacyBan: !sanctionBlocked && legacyBanned ? { reason: legacyBanReason } : null,
      reload,
    };
  }, [active, history, legacyBanned, legacyBanReason, reload]);
  return <OrdererSanctionContext.Provider value={value}>{children}</OrdererSanctionContext.Provider>;
};
